import React, { useState } from 'react';
import { 
  DocumentTextIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import ClassificationBadge from './ClassificationBadge';
import AuthorityReviewFlag from './AuthorityReviewFlag';
import SCGCitation from './SCGCitation';

const SanitizationReasoning = ({ tearlineResult, onViewRuleFlow }) => {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'iterations', 'insights'
  const [expandedIterations, setExpandedIterations] = useState(new Set());
  const [showClassificationAnalysis, setShowClassificationAnalysis] = useState(new Set());

  if (!tearlineResult) {
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
        <div className="text-center py-8">
          <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Sanitization Data</h3>
          <p className="mt-1 text-sm text-gray-500">
            Run a tearline sanitization to see detailed reasoning and SCG rule analysis.
          </p>
        </div>
      </div>
    );
  }

  // Function to replace SCG rule IDs with SCGCitation components
  const renderScgRulesInText = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Regex to match SCG rule patterns: SCG-XXX, SCG-UNK-XXXXXX-XXX, etc.
    const scgRulePattern = /(SCG-[A-Z0-9-]+)/g;
    
    const parts = text.split(scgRulePattern);
    
    return parts.map((part, index) => {
      if (scgRulePattern.test(part)) {
        return (
          <SCGCitation 
            key={index} 
            ruleId={part} 
            compact={true} 
          />
        );
      }
      return part;
    });
  };

  const toggleIterationExpansion = (iterationNumber) => {
    const newExpanded = new Set(expandedIterations);
    if (newExpanded.has(iterationNumber)) {
      newExpanded.delete(iterationNumber);
    } else {
      newExpanded.add(iterationNumber);
    }
    setExpandedIterations(newExpanded);
  };

  const toggleClassificationAnalysis = (iterationNumber) => {
    const newExpanded = new Set(showClassificationAnalysis);
    if (newExpanded.has(iterationNumber)) {
      newExpanded.delete(iterationNumber);
    } else {
      newExpanded.add(iterationNumber);
    }
    setShowClassificationAnalysis(newExpanded);
  };


  const expandAllIterations = () => {
    const allIterations = new Set(tearlineResult.iterations?.map(i => i.iteration_number) || []);
    setExpandedIterations(allIterations);
  };

  const collapseAllIterations = () => {
    setExpandedIterations(new Set());
  };

  // Extract all unique SCG rules from iterations
  const getAllTriggeredRules = () => {
    const allRules = new Set();
    tearlineResult.iterations?.forEach(iteration => {
      iteration.scg_rules_triggered?.forEach(rule => allRules.add(rule));
    });
    return Array.from(allRules);
  };

  // Extract all changes made across iterations
  const getAllChanges = () => {
    const allChanges = [];
    tearlineResult.iterations?.forEach(iteration => {
      iteration.changes_made?.forEach(change => {
        allChanges.push({
          ...change,
          iteration: iteration.iteration_number
        });
      });
    });
    return allChanges;
  };

  const renderOverview = () => {
    const allRules = getAllTriggeredRules();
    const allChanges = getAllChanges();
    
    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-900">Total Iterations</p>
                <p className="text-2xl font-semibold text-blue-600">
                  {tearlineResult.iterations?.length || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center">
              <ShieldCheckIcon className="h-8 w-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-orange-900">SCG Rules Triggered</p>
                <p className="text-2xl font-semibold text-orange-600">
                  {allRules.length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-900">Target Achieved</p>
                <p className="text-2xl font-semibold text-green-600">
                  {tearlineResult.success ? 'Yes' : 'No'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Original vs Final Classification */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-lg font-medium text-gray-900 mb-3">Classification Journey</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Original Classification</label>
              <ClassificationBadge 
                classification={tearlineResult.original_classification?.classification}
                fullClassification={tearlineResult.original_classification?.full_classification}
                caveats={tearlineResult.original_classification?.caveats || []}
                releasability={tearlineResult.original_classification?.releasability || []}
                confidence={tearlineResult.original_classification?.confidence}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Final Classification</label>
              <ClassificationBadge 
                classification={tearlineResult.final_classification?.classification}
                fullClassification={tearlineResult.final_classification?.full_classification}
                caveats={tearlineResult.final_classification?.caveats || []}
                releasability={tearlineResult.final_classification?.releasability || []}
                confidence={tearlineResult.final_classification?.confidence}
              />
            </div>
          </div>
        </div>

        {/* Key SCG Rules */}
        {allRules.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-3">Key SCG Rules Involved</h4>
            <div className="flex flex-wrap gap-2">
              {allRules.map((rule, index) => (
                <SCGCitation 
                  key={index} 
                  ruleId={rule} 
                  compact={true} 
                />
              ))}
            </div>
          </div>
        )}

        {/* Changes Summary */}
        {allChanges.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-3">Sanitization Changes Made</h4>
            <div className="space-y-2">
              {allChanges.map((change, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                      change.type === 'redaction' ? 'bg-red-100 text-red-800' :
                      change.type === 'removal' ? 'bg-yellow-100 text-yellow-800' :
                      change.type === 'generalization' ? 'bg-blue-100 text-blue-800' :
                      change.type === 'explanation' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {change.type}
                    </span>
                    <span className="text-sm text-gray-700">{change.description}</span>
                  </div>
                  <span className="text-xs text-gray-500">Iteration {change.iteration}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Group iterations by segment for better UX
  const groupIterationsBySegment = () => {
    if (!tearlineResult.iterations || tearlineResult.iterations.length === 0) {
      return {};
    }

    const segmentGroups = {};
    tearlineResult.iterations.forEach(iteration => {
      const segmentId = iteration.segment_id !== undefined ? iteration.segment_id : 'main';
      if (!segmentGroups[segmentId]) {
        segmentGroups[segmentId] = [];
      }
      segmentGroups[segmentId].push(iteration);
    });

    // Sort iterations within each segment by segment_iteration number
    Object.keys(segmentGroups).forEach(segmentId => {
      segmentGroups[segmentId].sort((a, b) => 
        (a.segment_iteration || a.iteration_number) - (b.segment_iteration || b.iteration_number)
      );
    });

    return segmentGroups;
  };

  const renderIterations = () => {
    if (!tearlineResult.iterations || tearlineResult.iterations.length === 0) {
      return (
        <div className="text-center py-8">
          <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Iterations Available</h3>
          <p className="mt-1 text-sm text-gray-500">
            No sanitization iterations were recorded.
          </p>
        </div>
      );
    }

    const segmentGroups = groupIterationsBySegment();
    const hasSegments = Object.keys(segmentGroups).length > 1 || Object.keys(segmentGroups)[0] !== 'main';

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-medium text-gray-900">
            Sanitization Iterations ({tearlineResult.iterations.length})
          </h4>
          <div className="flex space-x-2">
            <button
              onClick={expandAllIterations}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Expand All
            </button>
            <span className="text-gray-400">|</span>
            <button
              onClick={collapseAllIterations}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Collapse All
            </button>
          </div>
        </div>

        {hasSegments ? (
          // Render grouped by segments
          Object.keys(segmentGroups).sort((a, b) => parseInt(a) - parseInt(b)).map(segmentId => (
            <div key={segmentId} className="border border-gray-200 rounded-lg bg-gray-50">
              <div className="px-4 py-3 bg-blue-50 rounded-t-lg border-b border-gray-200">
                <h5 className="font-medium text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-blue-600 mr-2" />
                  Segment {parseInt(segmentId) + 1}
                  <span className="ml-2 text-sm text-gray-600">
                    ({segmentGroups[segmentId].length} iteration{segmentGroups[segmentId].length !== 1 ? 's' : ''})
                  </span>
                </h5>
              </div>
              <div className="p-2 space-y-2">
                {segmentGroups[segmentId].map((iteration, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg bg-white">
                    <div className="px-4 py-3 bg-white rounded-t-lg flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="font-medium text-gray-900">
                          Iteration {iteration.segment_iteration || iteration.iteration_number}
                        </span>
                        <ClassificationBadge 
                          classification={iteration.classification_result?.classification}
                          fullClassification={iteration.classification_result?.full_classification}
                          caveats={iteration.classification_result?.caveats || []}
                          releasability={iteration.classification_result?.releasability || []}
                          confidence={iteration.classification_result?.confidence}
                          className="text-xs"
                        />
                        {iteration.meets_target ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <ClockIcon className="h-5 w-5 text-yellow-500" />
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {/* View Rule Flow Button */}
                        {onViewRuleFlow && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewRuleFlow(iteration.iteration_number - 1); // Convert to 0-based index
                            }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            title="View SCG Rule Hopping Graph for this iteration"
                          >
                            <ArrowTopRightOnSquareIcon className="w-3 h-3 mr-1" />
                            Rule Flow
                          </button>
                        )}
                        
                        {/* Expand/Collapse Button */}
                        <button
                          onClick={() => toggleIterationExpansion(iteration.iteration_number)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          {expandedIterations.has(iteration.iteration_number) ? (
                            <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    {expandedIterations.has(iteration.iteration_number) && (
                      <div className="px-4 pb-4 border-t border-gray-100">
                        <div className="mt-3 space-y-4">
                          {/* Iteration Summary */}
                          <div className="bg-gray-50 rounded-lg p-3">
                            <h6 className="text-sm font-medium text-gray-900 mb-2">Iteration Summary</h6>
                            <div className="grid grid-cols-2 gap-4 text-xs">
                              <div>
                                <span className="text-gray-500">Target Met:</span>
                                <span className={`ml-2 font-medium ${iteration.meets_target ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {iteration.meets_target ? 'Yes' : 'No'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">Changes Made:</span>
                                <span className="ml-2 font-medium text-gray-900">
                                  {iteration.changes_made?.length || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <h6 className="text-sm font-medium text-gray-900 mb-1">Strategy Used</h6>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {iteration.strategy_used || 'Unknown'}
                            </span>
                          </div>
                          
                          {iteration.reasoning && (
                            <div>
                              <h6 className="text-sm font-medium text-gray-900 mb-1">Reasoning</h6>
                              <p className="text-sm text-gray-700">{renderScgRulesInText(iteration.reasoning)}</p>
                            </div>
                          )}
                          
                          {iteration.changes_made && iteration.changes_made.length > 0 && (
                            <div>
                              <h6 className="text-sm font-medium text-gray-900 mb-1">Changes Made</h6>
                              <ul className="space-y-1">
                                {iteration.changes_made.map((change, changeIndex) => (
                                  <li key={changeIndex} className="text-sm text-gray-600 flex items-start">
                                    <span className="font-medium mr-2 capitalize">{change.type}:</span>
                                    <span>{change.description}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {iteration.scg_rules_triggered && iteration.scg_rules_triggered.length > 0 && (
                            <div>
                              <h6 className="text-sm font-medium text-gray-900 mb-2">SCG Rules Referenced</h6>
                              <div className="flex flex-wrap gap-2">
                                {iteration.scg_rules_triggered.map((rule, ruleIndex) => (
                                  <SCGCitation key={ruleIndex} ruleId={rule} compact={true} />
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Tearline Draft for Segmented Iterations */}
                          <div>
                            <h6 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              Tearline Draft (Iteration {iteration.segment_iteration || iteration.iteration_number})
                            </h6>
                            <div className="text-sm text-gray-800 bg-blue-50 border border-blue-200 p-3 rounded-lg max-h-48 overflow-y-auto">
                              <div className="mb-2 text-xs text-blue-600 font-medium">
                                Classification: {iteration.classification_result?.full_classification || iteration.classification_result?.classification || 'Unknown'}
                              </div>
                              <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed">
                                {iteration.sanitized_text || 'No tearline draft available for this iteration'}
                              </pre>
                            </div>
                            {iteration.sanitized_text && (
                              <div className="mt-1 text-xs text-gray-500 flex items-center justify-between">
                                <span>Characters: {iteration.sanitized_text.length}</span>
                                <span>Words: {iteration.sanitized_text.split(/\s+/).filter(word => word.length > 0).length}</span>
                              </div>
                            )}
                          </div>

                          {/* Classification Analysis Button and Content */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h6 className="text-sm font-medium text-gray-900 flex items-center">
                                <InformationCircleIcon className="h-4 w-4 mr-1" />
                                Classification Analysis
                              </h6>
                              <button
                                onClick={() => toggleClassificationAnalysis(iteration.iteration_number)}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center space-x-1"
                              >
                                <span>{showClassificationAnalysis.has(iteration.iteration_number) ? 'Hide' : 'Show'} Analysis</span>
                                {showClassificationAnalysis.has(iteration.iteration_number) ? (
                                  <ChevronUpIcon className="h-3 w-3" />
                                ) : (
                                  <ChevronDownIcon className="h-3 w-3" />
                                )}
                              </button>
                            </div>
                            {showClassificationAnalysis.has(iteration.iteration_number) && (
                              <div className="text-sm text-gray-800 bg-yellow-50 border border-yellow-200 p-3 rounded-lg max-h-64 overflow-y-auto">
                                <div className="mb-2 text-xs text-yellow-700 font-medium">
                                  LLM Classification Reasoning for {iteration.classification_result?.full_classification || iteration.classification_result?.classification || 'Unknown'}
                                </div>
                                <div className="text-xs text-gray-700 leading-relaxed">
                                  {iteration.classification_result?.reasoning || 'No detailed classification reasoning available for this iteration.'}
                                </div>
                                {iteration.classification_result?.confidence && (
                                  <div className="mt-2 text-xs text-gray-600">
                                    <strong>Confidence:</strong> {Math.round(iteration.classification_result.confidence * 100)}%
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          // Fallback to flat rendering for non-segmented processing
          tearlineResult.iterations.map((iteration, index) => (
            <div key={index} className="border border-gray-200 rounded-lg">
              <div className="px-4 py-3 bg-gray-50 rounded-t-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="font-medium text-gray-900">
                    Iteration {iteration.iteration_number}
                  </span>
                  <ClassificationBadge 
                    classification={iteration.classification_result?.classification}
                    fullClassification={iteration.classification_result?.full_classification}
                    caveats={iteration.classification_result?.caveats || []}
                    releasability={iteration.classification_result?.releasability || []}
                    confidence={iteration.classification_result?.confidence}
                    className="text-xs"
                  />
                  {iteration.meets_target ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <ClockIcon className="h-5 w-5 text-yellow-500" />
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  {/* View Rule Flow Button */}
                  {onViewRuleFlow && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewRuleFlow(iteration.iteration_number - 1); // Convert to 0-based index
                      }}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      title="View SCG Rule Hopping Graph for this iteration"
                    >
                      <ArrowTopRightOnSquareIcon className="w-3 h-3 mr-1" />
                      Rule Flow
                    </button>
                  )}
                  
                  {/* Expand/Collapse Button */}
                  <button
                    onClick={() => toggleIterationExpansion(iteration.iteration_number)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {expandedIterations.has(iteration.iteration_number) ? (
                      <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

            {expandedIterations.has(iteration.iteration_number) && (
              <div className="px-4 py-3 border-t border-gray-200">
                <div className="space-y-4">
                  {/* Iteration Summary */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h6 className="text-sm font-medium text-gray-900 mb-2">Iteration Summary</h6>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <span className="text-gray-500">Target Met:</span>
                        <span className={`ml-2 font-medium ${iteration.meets_target ? 'text-green-600' : 'text-yellow-600'}`}>
                          {iteration.meets_target ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Changes Made:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {iteration.changes_made?.length || 0}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Strategy:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {iteration.strategy_used || 'Unknown'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">SCG Rules:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {iteration.scg_rules_triggered?.length || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Reasoning */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Sanitization Reasoning
                    </label>
                    <AuthorityReviewFlag 
                      reasoning={iteration.reasoning} 
                      variant="compact" 
                      className="mb-2" 
                    />
                    <div className="mt-1 text-sm text-gray-700 bg-gray-50 p-3 rounded">
                      {renderScgRulesInText(iteration.reasoning || 'No reasoning available')}
                    </div>
                  </div>

                  {/* Changes Made */}
                  {iteration.changes_made && iteration.changes_made.length > 0 && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Changes Made
                      </label>
                      <div className="mt-1 space-y-2">
                        {iteration.changes_made.map((change, changeIndex) => (
                          <div key={changeIndex} className="flex items-start space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                              change.type === 'redaction' ? 'bg-red-100 text-red-800' :
                              change.type === 'removal' ? 'bg-yellow-100 text-yellow-800' :
                              change.type === 'generalization' ? 'bg-blue-100 text-blue-800' :
                              change.type === 'explanation' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {change.type}
                            </span>
                            <div className="flex-1">
                              <p className="text-sm text-gray-700">{change.description}</p>
                              {change.reason && (
                                <p className="text-xs text-gray-500 mt-1">{change.reason}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* SCG Rules Triggered */}
                  {iteration.scg_rules_triggered && iteration.scg_rules_triggered.length > 0 && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        SCG Rules Triggered
                      </label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {iteration.scg_rules_triggered.map((rule, ruleIndex) => (
                          <SCGCitation 
                            key={ruleIndex} 
                            ruleId={rule} 
                            compact={true} 
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tearline Draft */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-1" />
                      Tearline Draft (Iteration {iteration.iteration_number})
                    </label>
                    <div className="mt-2 text-sm text-gray-800 bg-blue-50 border border-blue-200 p-4 rounded-lg max-h-60 overflow-y-auto">
                      <div className="mb-2 text-xs text-blue-600 font-medium">
                        Classification: {iteration.classification_result?.full_classification || iteration.classification_result?.classification || 'Unknown'}
                      </div>
                      <pre className="whitespace-pre-wrap font-sans leading-relaxed">
                        {iteration.sanitized_text || 'No tearline draft available for this iteration'}
                      </pre>
                    </div>
                    {iteration.sanitized_text && (
                      <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
                        <span>Character count: {iteration.sanitized_text.length}</span>
                        <span>Word count: {iteration.sanitized_text.split(/\s+/).filter(word => word.length > 0).length}</span>
                      </div>
                    )}
                  </div>

                  {/* Classification Analysis Button and Content */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center">
                        <InformationCircleIcon className="h-4 w-4 mr-1" />
                        Classification Analysis
                      </label>
                      <button
                        onClick={() => toggleClassificationAnalysis(iteration.iteration_number)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center space-x-1"
                      >
                        <span>{showClassificationAnalysis.has(iteration.iteration_number) ? 'Hide' : 'Show'} Analysis</span>
                        {showClassificationAnalysis.has(iteration.iteration_number) ? (
                          <ChevronUpIcon className="h-3 w-3" />
                        ) : (
                          <ChevronDownIcon className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                    {showClassificationAnalysis.has(iteration.iteration_number) && (
                      <div className="text-sm text-gray-800 bg-yellow-50 border border-yellow-200 p-4 rounded-lg max-h-64 overflow-y-auto">
                        <div className="mb-2 text-xs text-yellow-700 font-medium">
                          LLM Classification Reasoning for {iteration.classification_result?.full_classification || iteration.classification_result?.classification || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {iteration.classification_result?.reasoning || 'No detailed classification reasoning available for this iteration.'}
                        </div>
                        {iteration.classification_result?.confidence && (
                          <div className="mt-2 text-xs text-gray-600">
                            <strong>Confidence:</strong> {Math.round(iteration.classification_result.confidence * 100)}%
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))
        )}
      </div>
    );
  };

  const renderInsights = () => {
    const explanation = tearlineResult?.explanation;

    if (!explanation) {
      return (
        <div className="text-center py-8">
          <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-600" />
          <h3 className="mt-2 text-sm font-medium text-gray-300">No Insights Available</h3>
          <p className="mt-1 text-sm text-gray-500">
            Detailed insights will be available after processing completes.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="bg-gray-800 rounded-lg p-4 border border-amber-700/30">
          <h4 className="text-lg font-semibold text-amber-400 mb-2">Summary</h4>
          <p className="text-sm text-gray-300 leading-relaxed">{explanation.summary}</p>
        </div>

        {/* Confidence Assessment */}
        {explanation.confidence_assessment && (
          <div className={`rounded-lg p-4 border ${
            explanation.confidence_assessment.startsWith('HIGH')
              ? 'bg-green-900/30 border-green-700/50'
              : explanation.confidence_assessment.startsWith('MEDIUM')
              ? 'bg-yellow-900/30 border-yellow-700/50'
              : 'bg-red-900/30 border-red-700/50'
          }`}>
            <h4 className="text-sm font-semibold text-gray-200 mb-1">Confidence Assessment</h4>
            <p className="text-sm text-gray-300">{explanation.confidence_assessment}</p>
          </div>
        )}

        {/* Classification Journey */}
        {explanation.classification_journey && explanation.classification_journey.length > 0 && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-200 mb-3">Classification Journey</h4>
            <div className="space-y-3">
              {explanation.classification_journey.map((step, index) => (
                <div key={index} className="flex items-center space-x-4 p-3 bg-gray-900 rounded-lg">
                  <div className="flex-shrink-0 w-8 h-8 bg-amber-700/30 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold text-amber-400">{step.iteration}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-200">{step.from}</span>
                      <span className="text-gray-500">→</span>
                      <span className={`text-sm font-medium ${
                        step.progress === 'improved' ? 'text-green-400' :
                        step.progress === 'regressed' ? 'text-red-400' : 'text-gray-400'
                      }`}>{step.to}</span>
                      {step.progress === 'improved' && (
                        <CheckCircleIcon className="h-4 w-4 text-green-400" />
                      )}
                    </div>
                    {step.key_actions && step.key_actions.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Actions: {step.key_actions.join(', ')}
                      </p>
                    )}
                    {step.blocking_rules && step.blocking_rules.length > 0 && (
                      <p className="text-xs text-amber-500 mt-1">
                        Blocking rules: {step.blocking_rules.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Changes */}
        {explanation.key_changes && explanation.key_changes.length > 0 && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-200 mb-3">Key Changes Made</h4>
            <div className="space-y-2">
              {explanation.key_changes.map((change, index) => (
                <div key={index} className="flex items-start space-x-3 p-2 hover:bg-gray-700/50 rounded">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    change.type === 'removal' ? 'bg-red-900/40 text-red-300' :
                    change.type === 'redaction' ? 'bg-orange-900/40 text-orange-300' :
                    change.type === 'generalization' ? 'bg-blue-900/40 text-blue-300' :
                    'bg-gray-700 text-gray-300'
                  }`}>
                    {change.type}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-300">{change.description}</p>
                    <p className="text-xs text-gray-500 mt-1">{change.impact}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SCG Rule Impacts */}
        {explanation.scg_rule_impacts && explanation.scg_rule_impacts.length > 0 && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-200 mb-3">SCG Rule Analysis</h4>
            <div className="space-y-2">
              {explanation.scg_rule_impacts.map((rule, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-900 rounded">
                  <div className="flex items-center space-x-2">
                    <SCGCitation ruleId={rule.rule_id} compact={true} />
                    <span className="text-sm text-gray-400">{rule.description}</span>
                  </div>
                  <span className={`text-xs font-medium ${
                    rule.addressed ? 'text-green-400' : 'text-amber-400'
                  }`}>
                    {rule.addressed ? 'Addressed' : 'Still Active'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {explanation.recommendations && explanation.recommendations.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mr-2" />
              Recommendations
            </h4>
            <div className="space-y-3">
              {explanation.recommendations.map((rec, index) => (
                <div key={index} className="p-3 bg-white rounded border border-amber-100">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      rec.priority === 'HIGH' ? 'bg-red-100 text-red-800' :
                      rec.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {rec.priority}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{rec.action}</span>
                  </div>
                  <p className="text-sm text-gray-600">{rec.details}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200">
      {/* Header */}
      <div className="bg-orange-50 border-b border-orange-200 px-6 py-4">
        <div className="flex items-center space-x-3">
          <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Sanitization Analysis & Reasoning
            </h3>
            <p className="text-sm text-gray-600">
              Detailed breakdown of sanitization process and SCG rule guidance
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('overview')}
            className={`${
              activeTab === 'overview'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <InformationCircleIcon className="h-4 w-4" />
            <span>Overview</span>
          </button>
          <button
            onClick={() => setActiveTab('iterations')}
            className={`${
              activeTab === 'iterations'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <DocumentTextIcon className="h-4 w-4" />
            <span>Iterations</span>
          </button>
          <button
            onClick={() => setActiveTab('insights')}
            className={`${
              activeTab === 'insights'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <ShieldCheckIcon className="h-4 w-4" />
            <span>Insights</span>
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'iterations' && renderIterations()}
        {activeTab === 'insights' && renderInsights()}
      </div>
    </div>
  );
};

export default SanitizationReasoning;